import DOMPurify from "dompurify";

/**
 * Sanitize HTML content for safe rendering with v-html
 */
export const sanitizeHtml = (dirty: string): string => {
  return DOMPurify.sanitize(dirty);
};

/**
 * Sanitize a plain text input: trim whitespace and strip all HTML tags
 */
export const sanitizeInput = (value: string): string => {
  if (typeof value !== "string") return value;
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: [] }).trim();
};

/**
 * A set of object keys that should not be sanitized. These fields are not
 * expected to contain HTML and sanitizing them might corrupt data like tokens or passwords.
 * This set can be expanded based on application needs.
 */
const UNSANITIZED_KEYS = new Set([
  "token",
  "password",
  "newPassword",
  "turnstileToken",
  "email",
  "userId",
]);

/**
 * Recursively sanitize all string values in an object (for form payloads)
 */
export const sanitizePayload = <T extends Record<string, unknown>>(
  obj: T
): T => {
  const result = { ...obj };
  for (const key of Object.keys(result)) {
    if (typeof result[key] === "string" && !UNSANITIZED_KEYS.has(key)) {
      (result as Record<string, unknown>)[key] = sanitizeInput(
        result[key] as string
      );
    } else if (Array.isArray(result[key])) {
      (result as Record<string, unknown>)[key] = (result[key] as unknown[]).map(
        (item) =>
          typeof item === "string"
            ? sanitizeInput(item)
            : typeof item === "object" && item !== null
            ? sanitizePayload(item as Record<string, unknown>)
            : item
      );
    } else if (result[key] && typeof result[key] === "object") {
      (result as Record<string, unknown>)[key] = sanitizePayload(
        result[key] as Record<string, unknown>
      );
    }
  }
  return result;
};

export default { sanitizeHtml, sanitizeInput, sanitizePayload };
